import { useState } from 'react';

import Title from '@zenbusiness/common-components/Title';
import PropTypes from 'prop-types';
import { Navigate } from 'react-router-dom';

import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Hidden from '@mui/material/Hidden';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import makeStyles from '@mui/styles/makeStyles';

import SharedAsset from '#client/components/SharedAsset';
import TextPlaceholder from '#client/components/TextPlaceholder';
import { getDerivedStatus } from '#client/utils/orderTask';
import { trackLinkPressed } from '#client/utils/reporting';
import { buildCartUrl } from '#client/utils/urls';

import AccountingFaq from './AccountingFaq';
import AccountingPlanDetailsModal from './AccountingPlanDetailsModal';

const useStyles = makeStyles((theme) => ({
  root: {
    paddingTop: 16,
    [theme.breakpoints.up('md')]: {
      padding: '32px 34px'
    },
    '& .MuiAccordion-root:before': {
      'background-color': 'transparent'
    }
  },
  card: {
    padding: 32,
    marginTop: 32,
    marginBottom: 32
  },
  panel: {
    marginTop: 16,
    marginBottom: 16,
    border: '2px solid #F1F4F5',
    borderRadius: 4,
    boxShadow: 'none'
  },
  accountAssessmentAction: {
    [theme.breakpoints.down('md')]: {
      width: '100%',
      marginTop: 24,
      marginBottom: 0
    }
  },
  logoItem: {
    minWidth: '90px',
    [theme.breakpoints.up('md')]: {
      marginRight: 16
    },
    '& img': {
      width: 75
    }
  },
  accountingAssessmentText: {
    [theme.breakpoints.down('md')]: {
      textAlign: 'center'
    }
  },
  planLogo: {
    width: '100%',
    textAlign: 'center',
    [theme.breakpoints.up('md')]: {
      width: '80px',
      marginRight: '16px'
    },
    '& img': {
      width: 80
    }
  },
  pill: {
    marginLeft: 16,
    backgroundColor: '#C0E2E3',
    borderRadius: 16,
    color: '#28939C',
    textAlign: 'center',
    fontWeight: 'bold',
    padding: '6px 14px'
  },
  planName: {
    fontWeight: 'bold',
    [theme.breakpoints.down('md')]: {
      display: 'block'
    }
  },
  viewPlanAction: {
    [theme.breakpoints.down('md')]: {
      width: '100%',
      marginTop: 24,
      marginBottom: 0
    }
  },
  planShortcuts: {
    marginTop: 24,
    marginBottom: 24,
    [theme.breakpoints.up('md')]: {
      margin: 0,
      padding: 32
    }
  },
  planShortcutContent: {
    textAlign: 'center',
    minWidth: '285px',
    [theme.breakpoints.up('md')]: {
      textAlign: 'left'
    }
  },
  planShortcutAction: {
    marginTop: 24,
    [theme.breakpoints.up('md')]: {
      marginTop: 4
    }
  }
}));

const messages = {
  accountingType: {
    accounting: 'Accounting',
    accounting_grow_and_scale: 'Grow and Scale',
    accounting_quick_start: 'Quick Start',
    accounting_up_and_running: 'Up and Running'
  },
  accountingStatus: {
    active: 'Active',
    pending: 'Pending',
    inactive: 'Inactive'
  },
  cpaAssessment: {
    active: {
      title: 'Your Accounting Consultation Is Complete',
      details: ' '
    },
    pending: {
      title: 'Your Accounting Consultation Has Started',
      details: 'Please check your email to complete an assessment about your business.'
    },
    inactive: {
      title: 'Get a Free Accounting Consultation',
      details: 'Let us know you’re ready and we will send an email to get you started.'
    }
  },
  placeholders: {
    accountingStatus: '*********',
    accountingType: '************ ******',
    cpaAssessment: {
      title: '**** *********** *********** *** *********',
      details: '****** ***** **** ***** ** ******* *** *********** *** **** *********.'
    }
  }
};

const Accounting = ({
  loading,
  businessEntityUuid,
  accountingOrderTask,
  cpaAssessmentOrderTask
}) => {
  const classes = useStyles();
  const [modalOpen, toggleModal] = useState(false);

  const openModal = () => toggleModal(true);
  const closeModal = () => toggleModal(false);
  const accountingUrl = buildCartUrl('accounting', { businessEntityUuid, returnTo: encodeURIComponent(`/d/company/${businessEntityUuid}/accounting`) });
  const {
    orderTaskType: accountingType
    // ,
    // createdDatetime: accountingCreated
  } = accountingOrderTask || {};
  // const renewalDate = addYears(new Date(accountingCreated), '1');
  const nonLegacyAccountType = accountingType && accountingType !== 'accounting';
  const accountingDerivedState = !loading ? getDerivedStatus(accountingOrderTask) : null;
  const cpaAssessmentDerivedState = !loading ? getDerivedStatus(cpaAssessmentOrderTask) : null;

  // If the user doesn't have a accounting order task, kick back to dashboard
  if (!loading && !accountingType) {
    return <Navigate to={`/d/company/${businessEntityUuid}/`} />;
  }

  const handlePlanView = () => {
    openModal();
  };

  const handleReadyClick = (event) => trackLinkPressed({
    destination: accountingUrl
  }, event);

  return (
    <div className={classes.root}>
      <Grid
        container
        direction="row"
        justifyContent="flex-start"
        alignItems="center"
      >
        <Grid item container justifyContent="flex-start" alignItems="center" xs>
          <Title level={1} variant="lg-alt">
            Accounting
          </Title>
          <Typography variant="subtitle1" component="span" className={classes.pill}>
            {
              accountingDerivedState
                ? messages.accountingStatus[accountingDerivedState]
                : (<TextPlaceholder text={messages.placeholders.accountingStatus} />)
            }
          </Typography>
        </Grid>
        {/*
          Disabled until renewalDate understood
          <Grid item align="right">
            <Typography variant="subtitle1" color="textSecondary" align="right">
              RENEWS ON
            </Typography>
            <Typography variant="h4" align="right">
              {format(renewalDate, 'MM/dd/yyyy')}
            </Typography>
          </Grid>
        */}
      </Grid>
      <Paper className={classes.card}>
        <Grid
          container
          justifyContent="center"
          alignItems="center"
        >
          <Grid item className={classes.logoItem}>
            <SharedAsset src="/icon/product/initial-cpa-screening-256.svg" width={256} alt="" />
          </Grid>
          <Grid item xs={12} md>
            <Typography variant="h3" gutterBottom className={classes.accountingAssessmentText}>
              {
                cpaAssessmentDerivedState
                  ? messages.cpaAssessment[cpaAssessmentDerivedState].title
                  : (<TextPlaceholder text={messages.placeholders.cpaAssessment.title} />)
              }
            </Typography>
            <Typography variant="body2" color="textSecondary" className={classes.accountingAssessmentText}>
              {
                cpaAssessmentDerivedState
                  ? messages.cpaAssessment[cpaAssessmentDerivedState].details
                  : (<TextPlaceholder text={messages.placeholders.cpaAssessment.details} />)
              }
            </Typography>
          </Grid>
          {cpaAssessmentDerivedState === 'inactive' && (
            <Grid item align="right">
              <Button
                variant="text"
                color="primary"
                size="large"
                component="a"
                href={accountingUrl}
                onClick={handleReadyClick}
                className={classes.accountAssessmentAction}
              >
                I&apos;m Ready
              </Button>
            </Grid>
          )}
        </Grid>
      </Paper>
      <Paper className={classes.card}>
        <Grid
          container
          justifyContent="flex-start"
          alignItems="center"
          spacing={2}
        >
          <Grid item xs={12} md>
            <Typography variant="h3">
              Accounting Plan:
              &nbsp;
              <span className={classes.planName}>
                {loading ? (
                  <TextPlaceholder text={messages.placeholders.accountingType} />
                ) : `${messages.accountingType[accountingType] || messages.accountingType.accounting}`}
              </span>
            </Typography>
          </Grid>
          {nonLegacyAccountType && (
            <Hidden mdDown>
              <Grid item xs={12} md={4} align="right">
                <Button variant="contained" color="primary" onClick={handlePlanView}>View Plan Details</Button>
              </Grid>
            </Hidden>
          )}
        </Grid>
        <Divider />
        <Grid
          container
          justifyContent="space-evenly"
          alignItems="center"
          className={classes.planShortcuts}
          spacing={4}
        >
          <Grid item container justifyContent="center" alignItems="flex-start" xs={12} md>
            <Grid item className={classes.planLogo}>
              <SharedAsset src="/icon/misc/send-form-256.svg" width={256} alt="email" />
            </Grid>
            <Grid item xs className={classes.planShortcutContent}>
              <Typography variant="body1" color="textSecondary">
                Have a quick question and need to email an accountant? Send us an email.
              </Typography>
              <Button
                component="a"
                variant="text"
                href="mailto:accountant@zenbusiness.com"
                target="_blank"
                rel="noopener"
                size="small"
                className={classes.planShortcutAction}
              >
                accountant@zenbusiness.com
              </Button>
            </Grid>
          </Grid>
          <Grid item container justifyContent="center" alignItems="center" xs={12} md>
            <Grid item className={classes.planLogo}>
              <SharedAsset src="/icon/product/xero-account-256.svg" width={256} alt="xero" />
            </Grid>
            <Grid item xs className={classes.planShortcutContent}>
              <Typography variant="body1" color="textSecondary">
                Keep your books organized with Xero accounting software.
              </Typography>
              <Button
                component="a"
                variant="text"
                href="https://login.xero.com/identity/user/login/"
                target="_blank"
                rel="noopener"
                size="small"
                className={classes.planShortcutAction}
              >
                xero login
              </Button>
            </Grid>
          </Grid>
        </Grid>
        {nonLegacyAccountType && (
          <Hidden mdUp>
            <Button variant="contained" color="primary" onClick={handlePlanView} fullWidth>View Plan Details</Button>
          </Hidden>
        )}
      </Paper>
      <Paper className={classes.card}>
        <Grid
          container
          justifyContent="flex-start"
          alignItems="center"
          spacing={2}
        >
          <Grid item xs>
            <Typography variant="h3">
              Accounting FAQs
            </Typography>
          </Grid>
        </Grid>
        <Divider />
        <AccountingFaq />
      </Paper>
      <AccountingPlanDetailsModal
        classes={classes}
        open={modalOpen}
        onClose={closeModal}
        accountingType={accountingType}
      />
    </div>
  );
};

Accounting.propTypes = {
  loading: PropTypes.bool,
  businessEntityUuid: PropTypes.string.isRequired,
  accountingOrderTask: PropTypes.shape({}),
  cpaAssessmentOrderTask: PropTypes.shape({})
};

Accounting.defaultProps = {
  accountingOrderTask: null,
  cpaAssessmentOrderTask: null
};

export default Accounting;
